import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { PostRFI } from 'records/interactions/PostRFI';
import { Markup } from 'records/Markup';
import {
  createMarkup,
  deleteMarkup,
  getMarkup,
  getMarkups,
  updateMarkup,
} from 'services/markupService';

export interface MarkupActions {
  getSheetMarkups: (
    actionParams: AppStateActionParams,
    sheetId: string
  ) => AsyncGenerator<AppState>;

  getMarkup: (
    actionParams: AppStateActionParams,
    markupid: string,
    fileId: string
  ) => Promise<AppState>;

  setLocalMarkup: (
    actionParams: AppStateActionParams,
    markup: Markup
  ) => AppState;

  removeLocalMarkup: (
    actionParams: AppStateActionParams,
    markup: Markup
  ) => AppState;

  createMarkup: (
    actionParams: AppStateActionParams,
    markup: Markup
  ) => IterableIterator<AppState | Promise<AppState>>;

  updateMarkup: (
    actionParams: AppStateActionParams,
    updatedMarkup: Markup
  ) => Promise<AppState>;

  deleteMarkup: (
    actionParams: AppStateActionParams,
    markup: Markup
  ) => IterableIterator<AppState | Promise<AppState>>;
}

const markupActions = {
  getSheetMarkups: async function* getSheetMarkupsAction(
    { state, actions }: AppStateActionParams,
    pageId: string
  ): AsyncGenerator<AppState> {
    const markups = await getMarkups(pageId);

    const markupsMap = markups.reduce(
      (acc, m) => acc.set(m.markupid, m),
      Immutable.OrderedMap<string, Markup>()
    );

    yield state().mergeIn(['markups', pageId], markupsMap);

    // Since punches and rfi can be associated to markups, need to requery them.
    await actions.queryPunches();
    await actions.queryRFIs();

    yield state();
  },

  getMarkup: async (
    { state }: AppStateActionParams,
    markupId: string,
    fileId: string
  ): Promise<AppState> => {
    const markup = await getMarkup(markupId, fileId);

    return state().setIn(['markups', markup.fileid, markup.markupid], markup);
  },

  setLocalMarkup: (
    { state }: AppStateActionParams,
    markup: Markup
  ): AppState => {
    return state().setIn(['markups', markup.fileid, markup.markupid], markup);
  },

  removeLocalMarkup: (
    { state }: AppStateActionParams,
    markup: Markup
  ): AppState => {
    return state().deleteIn(['markups', markup.fileid, markup.markupid]);
  },

  createMarkup: function* createMarkupAction(
    { state }: AppStateActionParams,
    markup: Markup
  ): IterableIterator<AppState | Promise<AppState>> {
    // optimistically save.
    yield state().setIn(['markups', markup.fileid, markup.markupid], markup);

    yield (async (): Promise<AppState> => {
      const createdMarkup = await createMarkup(markup);

      return state().setIn(
        ['markups', createdMarkup.fileid, createdMarkup.markupid],
        createdMarkup
      );
    })();
  },

  // TODO, maybe add some optimistic updating here.
  updateMarkup: async (
    { state }: AppStateActionParams,
    updatedMarkup: Markup
  ): Promise<AppState> => {
    // need to make sure that the markup isn't part of an interaction, aka "local".
    const { interactions } = state();

    const createPunchInterMarkupIds = interactions
      .filter((i) => i.type === 'CreatePunch')
      .flatMap((i) =>
        (i as CreatePunch).punch.markups.map((m) => m.clientGeneratedID)
      );

    const postRFIInterMarkupIds = interactions
      .filter((i) => i.type === 'PostRFI')
      .map((i) => (i as PostRFI).markup?.markupid || '');

    if (
      !createPunchInterMarkupIds.includes(updatedMarkup.markupid) &&
      !postRFIInterMarkupIds.includes(updatedMarkup.markupid)
    ) {
      const nextMarkup = await updateMarkup(updatedMarkup);
      return state().setIn(
        ['markups', nextMarkup.fileid, nextMarkup.markupid],
        nextMarkup
      );
    }

    return state();
  },

  deleteMarkup: function* deleteMarkupAction(
    { state }: AppStateActionParams,
    markup: Markup
  ): IterableIterator<AppState | Promise<AppState>> {
    // optimistically delete.
    yield state().deleteIn(['markups', markup.fileid, markup.markupid]);

    yield (async (): Promise<AppState> => {
      const deletedMarkupId = await deleteMarkup(markup);

      // Need to check to make sure delete happened, else revert deletion.
      if (deletedMarkupId !== markup.markupid) {
        return state().setIn(
          ['markups', markup.fileid, markup.markupid],
          markup
        );
      }

      return state();
    })();
  },
};

export default markupActions;
